import { api, ApiError } from './client';
import type { BatchResponse, BatchSubRequest, BatchSubResponse } from './types';

/**
 * Unwrap a required batch sub-response.
 *
 * Throws a descriptive error when the sub-response is missing, carries a
 * non-2xx status, or has null data. This lets TanStack Query enter its error
 * state (and retry / show error UI) instead of silently rendering empty data
 * for a sub-request that the view depends on.
 */
export function unwrapBatch<T>(
    resp: BatchSubResponse<T> | undefined,
    label: string,
): T {
    if (!resp) {
        throw new Error(`Batch sub-request "${label}" is missing from the response.`);
    }
    if (resp.status < 200 || resp.status >= 300) {
        throw new Error(`Batch sub-request "${label}" failed with status ${resp.status}.`);
    }
    if (resp.data == null) {
        throw new Error(`Batch sub-request "${label}" returned no data.`);
    }
    return resp.data;
}

/**
 * Unwrap an optional batch sub-response, returning a fallback on failure.
 *
 * Use for genuinely-optional sub-requests where the UI degrades gracefully
 * when the data is absent. Preserves the tolerant behavior of returning a
 * default instead of surfacing an error.
 */
export function unwrapBatchOptional<T, F = T>(
    resp: BatchSubResponse<T> | undefined,
    fallback: F,
): T | F {
    if (!resp || resp.status < 200 || resp.status >= 300 || resp.data == null) {
        return fallback;
    }
    return resp.data;
}

/**
 * Wraps a single direct GET request in the batch response shape.
 * Catches ApiError to preserve the per-sub-request status contract
 * that batch hooks rely on (e.g., checking for 404).
 */
async function fetchSingle<T extends Record<string, unknown>>(
    req: BatchSubRequest,
): Promise<BatchResponse<T>> {
    try {
        const data = await api.get(req.path);
        return {
            responses: {
                [req.id]: { status: 200, data } as BatchSubResponse,
            },
        } as BatchResponse<T>;
    } catch (err) {
        if (err instanceof ApiError) {
            return {
                responses: {
                    [req.id]: { status: err.status, data: err.data } as BatchSubResponse,
                },
            } as BatchResponse<T>;
        }
        throw err;
    }
}

/**
 * Fetch a group of requests: direct GET if single, batch POST if multiple.
 */
function fetchGroup<T extends Record<string, unknown>>(
    requests: BatchSubRequest[],
    batchEndpoint: string,
): Promise<BatchResponse<T>> | null {
    if (requests.length === 0) return null;
    if (requests.length === 1) return fetchSingle<T>(requests[0]!);
    return api.post<BatchResponse<T>>(batchEndpoint, { requests });
}

/**
 * Make a batch request to the API.
 *
 * When the Go API toggle is enabled, requests are split by target:
 * - Go-eligible paths (/api/go/*) route to Go (direct or via /api/go/batch)
 * - PHP paths route to PHP (direct or via /api/batch)
 * - Both groups fire in parallel and results are merged.
 *
 * When the toggle is off (no /api/go/ paths), everything goes through
 * the PHP batch endpoint as before.
 */
export async function apiBatch<T extends Record<string, unknown>>(
    requests: BatchSubRequest[],
): Promise<BatchResponse<T>> {
    const goRequests = requests.filter(r => r.path.startsWith('/api/go/'));
    const phpRequests = requests.filter(r => !r.path.startsWith('/api/go/'));

    // Fast path: no Go requests — original behavior, single PHP batch.
    if (goRequests.length === 0) {
        return api.post<BatchResponse<T>>('/api/batch', { requests });
    }

    const promises: Promise<BatchResponse<T>>[] = [];

    const phpPromise = fetchGroup<T>(phpRequests, '/api/batch');
    if (phpPromise) promises.push(phpPromise);

    const goPromise = fetchGroup<T>(goRequests, '/api/go/batch');
    if (goPromise) promises.push(goPromise);

    const results = await Promise.all(promises);

    // Merge response maps from all groups.
    const merged = {} as Record<string, BatchSubResponse>;
    for (const result of results) {
        Object.assign(merged, result.responses);
    }

    return { responses: merged } as BatchResponse<T>;
}
